<template>
  <div class="icon-label-component" :class="rootClasses">
    <component :is="kindConfig.iconComponent" class="sls-icon" />
    <ellipsis-text :text="kindConfig.label" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw } from "vue";
import EnabledBlue16Icon from "@images/icons/indicative/16px/EnabledBlue16.svg?component";
import Error16Icon from "@images/icons/indicative/16px/Error16.svg?component";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import { useI18n } from "vue-i18n";

// Composable
const { t } = useI18n();

// Constants
const KIND_CONFIG = {
  online: {
    label: t("assessment_connection_online_label"),
    iconComponent: markRaw(EnabledBlue16Icon),
  },
  offline: {
    label: t("assessment_connection_offline_label"),
    iconComponent: markRaw(Error16Icon),
  },
} as const;

type Kind = keyof typeof KIND_CONFIG;

// Props
interface Props {
  kind?: Kind;
}

const { kind = "offline" } = defineProps<Props>();

// Computed
const rootClasses = computed(() => `${kind}`);
const kindConfig = computed(() => KIND_CONFIG[kind]);
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.icon-label-component {
  @include carbon--type-style("label-03", true);
  display: inline-flex;
  align-self: center;
  align-items: center;
  height: 1.75rem;
  padding: $spacing-02 $spacing-03;
  gap: $spacing-02;
  text-transform: uppercase;
  border-radius: 5px;
  cursor: default;

  &.online {
    background-color: tint($state-positive, 80%);
    color: shade($state-positive, 60%);
  }

  &.offline {
    background-color: tint($status-offline, 80%);
    color: shade($status-offline, 60%);
  }

  .sls-icon {
    min-width: $spacing-05;
    path {
      &:first-of-type {
        opacity: 0.1;
      }
    }
    [fill] {
      fill: currentColor;
    }
  }
}
</style>
